import type { Card, CardFinish, CardRarity } from "@/context/AppContext";

/**
 * Short-form rarity buckets used by Studio's per-bundle composition row.
 * Maps card.rarity values into the short codes shown in chip badges (C/UC/R/H/V/E/T).
 */
export type RarityShortCode = "C" | "UC" | "R" | "H" | "V" | "E" | "T";

export const RARITY_LABELS: Record<RarityShortCode, string> = {
  C: "Common",
  UC: "Uncommon",
  R: "Rare",
  H: "Holo",
  V: "Hit / Premium",
  E: "Energy",
  T: "Trainer",
};

export type RarityComposition = Record<RarityShortCode, number>;

/**
 * Maps a Card's (rarity, finish) pair to its display bucket. Reads finish
 * first because the two-axis refactor moved Holo / Reverse Holo / Premium
 * out of the rarity enum and onto `Card.finish`.
 */
export function rarityShortCode(
  rarity: CardRarity | string,
  finish?: CardFinish,
): RarityShortCode {
  if (finish === "Premium") return "V";
  if (finish === "Holo" || finish === "Reverse Holo") return "H";
  if (rarity === "Promo" || rarity === "Sealed") return "V";
  if (rarity === "Rare") return "R";
  if (rarity === "Uncommon") return "UC";
  if (rarity === "Energy") return "E";
  if (rarity === "Trainer") return "T";
  return "C";
}

/**
 * Sum card.quantity into rarity buckets. Used by Studio's drill-in to render
 * a "C: 60 · UC: 22 · R: 8 · H: 5" row beneath each bundle name.
 */
export function summarizeRarityCounts(cards: Card[]): RarityComposition {
  const out: RarityComposition = { C: 0, UC: 0, R: 0, H: 0, V: 0, E: 0, T: 0 };
  for (const c of cards) {
    out[rarityShortCode(c.rarity, c.finish)] += c.quantity;
  }
  return out;
}

/** Order short codes for stable display: most-valuable-looking first. */
export const RARITY_DISPLAY_ORDER: RarityShortCode[] = [
  "V",
  "H",
  "R",
  "UC",
  "C",
  "T",
  "E",
];
